import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalFieldId } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { computed, toValue } from 'vue';
import { resolveFieldDisplayFieldSelection } from '#layers/data-model/utils/display-template-field-selection';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { useFieldSelectionContexts } from '~/composables/use-field-selection-contexts';
import { idFieldSelectionToFieldSelection } from '~/utils/id-field-selection-to-field-selection';
import { mergeFieldSelections } from '~/utils/merge-field-selection';

/**
 * Compute precise FieldSelection for a set of visible field IDs on a collection.
 *
 * For each field, delegates to `resolveFieldDisplayFieldSelection` which handles
 * both primitive and relation fields, including display block `fields()` resolution.
 */
export function useFieldSelectionForFieldIds(
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>,
	visibleFieldIds: MaybeRefOrGetter<GlobalFieldId[]>,
) {
	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	return computed<FieldSelection>(() => {
		const col = toValue(collection);
		const fieldIds = toValue(visibleFieldIds);
		if (!col || fieldIds.length === 0) return ['*'];

		const columnSelections: FieldSelection[] = fieldIds.map((fieldId) => {
			const field = findCollectionFieldById(col, fieldId);

			if (!field) return [];

			const idSelection = resolveFieldDisplayFieldSelection(field, col, fieldSelectionCtx);
			return idFieldSelectionToFieldSelection(idSelection, idCtx);
		});

		return mergeFieldSelections(...columnSelections);
	});
}
